import React from 'react'
import Breadcrumbs from '../../components/common/Breadcrumbs/Breadcrumbs';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import Footer from '../../components/common/Footer/Footer';
import { markActiveInActiveAgencyUser } from '../../store/agencyStore/actions/markActiveInActiveAgencyUserActions';
import { useToast } from '../../hooks/useToast';
import { createAgencyUser } from '../../store/agencyStore/actions/createAgencyUserActions';

const UserAddAndEditPage = () => {

    const navigate = useNavigate();
    const location = useLocation();
    const dispatch = useDispatch();
    const Toast = useToast();
    const [errors, setErrors] = useState({});

    const [formValues, setFormValues] = useState({
        agencyUserId: "",
        firstName: "",
        lastName: "",
        phone: "",
        email: "",
        status: "",
        role: "",
    });

    const [isEdit, setIsEdit] = useState(false);

    // Simple field validators
    const validateField = (name, value) => {
        switch (name) {
            case 'firstName':
                if (!value?.trim()) return 'First name is required';
                if (!/^[A-Za-z]+$/.test(value.trim())) return 'First name should contain only alphabets';
                return '';
            case 'lastName':
                if (!value?.trim()) return 'Last name is required';
                if (!/^[A-Za-z]+$/.test(value.trim())) return 'Last name should contain only alphabets';
                return '';
            case 'phone':
                if (!value) return 'Phone number is required';
                if (!/^\d{10}$/.test(value)) return 'Enter a valid 10-digit phone number';
                return '';
            case 'email':
                if (!value) return 'Email is required';
                // Basic email pattern
                if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)) return 'Enter a valid email address';
                return '';
            case 'status':
                if (!value) return 'Status is required';
                return '';
            case 'role':
                if (!value) return 'Role is required';
                return '';
            default:
                return '';
        }
    };

    const validateForm = () => {
        const newErrors = {};
        const fns = ['firstName', 'lastName', 'phone', 'email', 'status', 'role'];
        fns.forEach((field) => {
            const msg = validateField(field, formValues[field]);
            if (msg) newErrors[field] = msg;
        });
        return newErrors;
    };

    const handleUpdateUser = () => {
        const payload = {
            userId: formValues.agencyUserId,
            enabled: formValues.status === "ACTIVE" ? true : false,
            role: formValues.role
        };
        dispatch(markActiveInActiveAgencyUser(payload, (status, response, message) => {
            if (status === 200) {
                navigate('/agency/user-management');
            } else {
                Toast(message, 'error');
            }
        }));
    };

    const handleAddUser = () => {
        const payload = {
            firstName: formValues.firstName,
            lastName: formValues.lastName,
            phone: formValues.phone,
            email: formValues.email,
            status: formValues.status === "ACTIVE" ? true : false,
            role: formValues.role
        };
        dispatch(createAgencyUser(payload, (status, response, message) => {
            if (status === 200) {
                navigate('/agency/user-management');
            } else {
                Toast(message, 'error');
            }
        }));
    };

    useEffect(() => {
        if (location.state?.user) {
            setFormValues(location.state.user);
            setIsEdit(true);
        }
    }, [location.state]);

    return (
        <div className="w-full h-full flex flex-col justify-start bg-[#EDEEF1]">
            <div className="w-full h-full flex flex-col bg-[#F2F3F7]">
                <Breadcrumbs
                    breadcrumbs={[
                        { label: "Home", href: "/" },
                        { label: "User Management", href: "/agency/user-management" },
                        {
                            label: isEdit ? "Edit User" : "Add User",
                            href: "/agency/user-management/add-edit",
                        },
                    ]}
                />
                <form className="w-full h-full p-8 shadow-md space-y-4 mt-5 overflow-scroll px-[4rem]">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <div>
                            <label className="block text-gray-700 inter-font font-[400] mb-2 text-[1.6rem]">
                                First Name
                            </label>
                            <input
                                required
                                readOnly={isEdit}
                                type="text"
                                name="firstName"
                                value={formValues.firstName}
                                onChange={(e) => {
                                    const value = e.target.value;
                                    setFormValues({ ...formValues, firstName: value });
                                    const msg = validateField('firstName', value);
                                    setErrors((prev) => ({ ...prev, firstName: msg }));
                                }}
                                placeholder="Enter First Name"
                                className="w-3/4 h-[4.8rem] border border-gray-300 inter-font font-[400] rounded-md p-[1.1rem] text-[1.6rem] focus:outline-none focus:ring focus:ring-green-400"
                            />
                            {errors.firstName ? (
                                <p className="text-red-500 text-lg mt-1">{errors.firstName}</p>
                            ) : null}

                        </div>
                        <div>
                            <label className="block text-gray-700 inter-font font-[400] mb-2 text-[1.6rem]">
                                Last Name
                            </label>
                            <input
                                required
                                readOnly={isEdit}
                                type="text"
                                name="lastName"
                                value={formValues.lastName}
                                onChange={(e) => {
                                    const value = e.target.value;
                                    setFormValues({ ...formValues, lastName: value });
                                    const msg = validateField('lastName', value);
                                    setErrors((prev) => ({ ...prev, lastName: msg }));
                                }}
                                placeholder="Enter Last Name"
                                className="w-3/4 h-[4.8rem] border border-gray-300 rounded-md p-[1.1rem] focus:outline-none focus:ring focus:ring-green-400 text-[1.6rem]"
                            />
                            {errors.lastName ? (
                                <p className="text-red-500 text-lg mt-1">{errors.lastName}</p>
                            ) : null}

                        </div>
                        <div>
                            <label className="block text-gray-700 inter-font font-[400] mb-2 text-[1.6rem]">
                                Phone Number
                            </label>
                            <input
                                required
                                readOnly={isEdit}
                                type="text"
                                maxLength={10}
                                name="phoneNumber"
                                value={formValues.phone}
                                onChange={(e) => {
                                    const value = e.target.value.replace(/\D/g, '');
                                    setFormValues({ ...formValues, phone: value });
                                    const msg = validateField('phone', value);
                                    setErrors((prev) => ({ ...prev, phone: msg }));
                                }}
                                placeholder="Enter Phone Number"
                                className="w-3/4 h-[4.8rem] border border-gray-300 rounded-md p-[1.1rem] focus:outline-none focus:ring focus:ring-green-400 text-[1.6rem]"
                            />
                            {errors.phone ? (
                                <p className="text-red-500 text-lg mt-1">{errors.phone}</p>
                            ) : null}

                        </div>
                        <div>
                            <label className="block text-gray-700 inter-font font-[400] mb-2 text-[1.6rem]">
                                Email
                            </label>
                            <input
                                required
                                readOnly={isEdit}
                                type="text"
                                name="email"
                                value={formValues.email}
                                onChange={(e) => {
                                    const value = e.target.value;
                                    setFormValues({ ...formValues, email: value });
                                    const msg = validateField('email', value);
                                    setErrors((prev) => ({ ...prev, email: msg }));
                                }}
                                placeholder="Enter Email Address"
                                className="w-3/4 h-[4.8rem] border border-gray-300 rounded-md p-[1.1rem] focus:outline-none focus:ring focus:ring-green-400 text-[1.6rem]"
                            />
                            {errors.email ? (
                                <p className="text-red-500 text-lg mt-1">{errors.email}</p>
                            ) : null}

                        </div>
                        <div>
                            <label className="block text-gray-700 inter-font font-[400] mb-2 text-[1.6rem]">
                                Status
                            </label>
                            <select
                                name="status"
                                value={formValues.status}
                                onChange={(e) => {
                                    const value = e.target.value;
                                    setFormValues({ ...formValues, status: value });
                                    const msg = validateField('status', value);
                                    setErrors((prev) => ({ ...prev, status: msg }));
                                }}
                                className="w-3/4 h-[4.8rem] border border-gray-300 rounded-md p-[1.1rem] focus:outline-none focus:ring focus:ring-green-400 text-[1.6rem]"
                            >
                                <option value="">Select Status</option>
                                <option value="ACTIVE">ACTIVE</option>
                                <option value="INACTIVE">INACTIVE</option>
                            </select>
                            {errors.status ? (
                                <p className="text-red-500 text-lg mt-1">{errors.status}</p>
                            ) : null}
                        </div>
                        <div>
                            <label className="block text-gray-700 inter-font font-[400] mb-2 text-[1.6rem]">
                                Role
                            </label>
                            <select
                                name="role"
                                value={formValues.role}
                                onChange={(e) => {
                                    const value = e.target.value;
                                    setFormValues({ ...formValues, role: value });
                                    const msg = validateField('role', value);
                                    setErrors((prev) => ({ ...prev, role: msg }));
                                }}
                                className="w-3/4 h-[4.8rem] border border-gray-300 rounded-md p-[1.1rem] focus:outline-none focus:ring focus:ring-green-400 text-[1.6rem]"
                            >
                                <option value="">Select Role</option>
                                <option value="Manager">Manager</option>
                                <option value="Viewer">Viewer</option>
                            </select>
                            {errors.role ? (
                                <p className="text-red-500 text-lg mt-1">{errors.role}</p>
                            ) : null}
                        </div>
                    </div>
                    <div className="flex justify-between">
                        <div className="py-[3rem]">
                            <button
                                className="border border-[#0D9B50] text-white bg-[#0D9B50] hover:scale-95 cursor-pointer px-6 py-4 rounded-[0.8rem] text-[1.6rem]"
                                onClick={(e) => {
                                    e.preventDefault();
                                    const formErrs = validateForm();
                                    setErrors(formErrs);
                                    if (Object.keys(formErrs).length > 0) {
                                        Toast('Please fix the errors', 'error');
                                        return;
                                    }
                                    if (!formValues.status || !formValues.role) {
                                        Toast('Please select Status and Role', 'error');
                                        return;
                                    }
                                    if (isEdit) {
                                        handleUpdateUser();
                                    } else {
                                        handleAddUser();
                                    }
                                }}
                            >
                                {isEdit ? "Update" : "Add"}
                            </button>
                        </div>
                    </div>
                </form>
            </div>
            <Footer />
        </div>
    )
}

export default UserAddAndEditPage